import * as React from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "@/lib/dexie-db";
import { pendingOutboxCount } from "@/lib/outbox";
import { cn } from "@/lib/utils";

/**
 * OfflineIndicator — sticky-top sliver. Drei states:
 *
 *  - online + no-pending:   nicht gerendert (silent)
 *  - online + pending:      gelber strip "X änderungen werden synchronisiert"
 *  - offline:               oranger strip "Offline · letzte sync vor X"
 *  - offline >5 tage:       roter strip mit iOS-eviction-warnung
 *
 * Saisonale-app + iOS-7-tage-storage-eviction = existenziell. Wenn der
 * user die app im sommer >7d nicht öffnet, kann iOS die IndexedDB
 * cleanen. → wir warnen ab tag 5.
 */

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const WARN_OFFLINE_AFTER_DAYS = 5;

function useOnlineStatus(): boolean {
  const [online, setOnline] = React.useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  React.useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

export function OfflineIndicator(): React.ReactElement | null {
  const online = useOnlineStatus();
  const pending = useLiveQuery(() => pendingOutboxCount(), [], 0);
  const lastSync = useLiveQuery(
    async () => (await db.syncMeta.get("last_sync"))?.timestamp ?? 0,
    [],
    0
  );
  const lastOnlineAt = useLastOnlineAt();

  // Record online-stamp on every render-while-online so the diff is fresh.
  React.useEffect(() => {
    if (online) {
      void db.syncMeta.put({ key: "last_online_at", timestamp: Date.now() });
    }
  }, [online]);

  if (online && pending === 0) return null;

  if (!online) {
    const daysOffline = lastOnlineAt
      ? Math.floor((Date.now() - lastOnlineAt) / (24 * 60 * 60 * 1000))
      : 0;
    const isCritical = daysOffline >= WARN_OFFLINE_AFTER_DAYS;

    return (
      <div
        role="status"
        aria-live="polite"
        className={cn(
          "sticky top-0 z-[var(--z-sticky)] w-full px-4 py-2 text-center text-sm font-medium",
          isCritical
            ? "bg-[color:var(--color-heat-4)] text-white"
            : "bg-[color:var(--color-heat-3)] text-white"
        )}
      >
        {isCritical ? (
          <>
            Offline seit {daysOffline} Tagen. Auf iOS können Daten verloren gehen wenn die App
            nicht regelmäßig geöffnet wird — bitte Verbindung herstellen.
          </>
        ) : (
          <>Offline. Änderungen werden lokal gespeichert und später synchronisiert.</>
        )}
      </div>
    );
  }

  // online but with pending mutations
  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-[var(--z-sticky)] w-full bg-[color:var(--color-heat-2)] px-4 py-2 text-center text-sm font-medium text-white"
    >
      {pending === 1
        ? "1 Änderung wird synchronisiert …"
        : `${pending} Änderungen werden synchronisiert …`}
      {lastSync > 0 && (
        <span className="ml-2 text-xs opacity-75">
          Letzte Synchronisation: {formatRelative(lastSync)}
        </span>
      )}
    </div>
  );
}

function useLastOnlineAt(): number {
  const value = useLiveQuery(
    async () => (await db.syncMeta.get("last_online_at"))?.timestamp ?? 0,
    [],
    0
  );
  return value;
}

function formatRelative(timestamp: number): string {
  const diff = Date.now() - timestamp;
  if (diff < 60_000) return "gerade eben";
  if (diff < 3_600_000) return `vor ${Math.floor(diff / 60_000)} Min`;
  if (diff < 24 * 3_600_000) return `vor ${Math.floor(diff / 3_600_000)} Std`;
  if (diff < SEVEN_DAYS_MS) return `vor ${Math.floor(diff / (24 * 3_600_000))} Tagen`;
  return new Date(timestamp).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
}
